import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { FC } from "react";
import { AdminFormFieldSubmit } from "../../../components/admin/formFields";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import { APP_CONFIG } from "../../../constants/constants";
import {
  reducerPath,
  useGetDeviceListingQuery,
} from "../../../api/network/camerasAdminVMApiService";
import { CamerasAdminVMApiTags } from "../../../api/network/vmApiService";
import { toast } from "react-toastify";
import { localStorageKeys, useLocalStorage } from "../../../utils/localStorageItems";
import { LegacyVerifyEmailOtpResponseSuccess } from "../../../api/types/Onboarding";
import { TLegacyUserWithAccount } from "../../../api/types/User";

type ModalVMCameraLiveviewContinueProps = {};

const ModalVMCameraLiveviewContinue: FC<
  ModalVMCameraLiveviewContinueProps
> = ({}) => {
  const { t: tMain } = useTranslation();
  const { t: tCamera } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.management.cameras",
  });
  const { t } = useTranslation("translation", {
    keyPrefix: "camera.modals.liveviewContinue",
  });
  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );
  const dispatch = useDispatch();

  const { getLocalStorageItem } = useLocalStorage();
  const loggedInUserData: LegacyVerifyEmailOtpResponseSuccess = useSelector(
    (state: any) => state.commonReducer.user
    );
  const loggedInUser: TLegacyUserWithAccount =
    loggedInUserData?.user ?? getLocalStorageItem(localStorageKeys.user);
  const liveviewDurationSeconds =
    loggedInUser?.account?.cameraliveviewduration ?? 30;
      
  const { data: dataDeviceListing, isFetching: isFetchingDeviceListing } =
    useGetDeviceListingQuery(
      {
        keyword: modalsState.showModalVMCameraLiveviewContinueSN || "",
        offset: 0,
        limit: 10,
      },
      {
        skip: !modalsState.showModalVMCameraLiveviewContinueSN,
        refetchOnMountOrArgChange: true, // caching removed to ensure listing always shows correct status
      }
    );

  const hideModal = () => {
    dispatch(
      setModalsData({
        ...modalsState,
        showModalVMCameraLiveviewContinueSN: null,
      })
    );
  };

  const onCameraWakeupSuccess = (serialNumber: string) => {
    // refetch camera device listing
    dispatch({
      type: `${reducerPath}/invalidateTags`,
      payload: [CamerasAdminVMApiTags.CAMERA_DEVICE_LISTING],
    });

    setTimeout(() => {
      dispatch(setModalsData({ ...modalsState, showPrimaryOverlay: true }));
      toast.info(
        "Allowing a few seconds for the camera to wake up and connect with servers..."
      );
      // if (!!serialNumber) {
      //   // the API uses `serial_number` instead of `cid`
      //   dispatch(
      //     setModalsData({
      //       ...modalsState,
      //       showModalVMCameraLiveviewContinueSN: null,
      //       showModalVMCameraVoicecallSN: serialNumber,
      //     })
      //   );
      // }
      setTimeout(() => {
        dispatch(setModalsData({ ...modalsState, showPrimaryOverlay: false }));
        // Initiate Camera live view for currently selected camera
        if (!!serialNumber) {
          dispatch(
            setModalsData({
              ...modalsState,
              showModalWakeupCamera: null,
              showModalVMCameraLiveviewContinueSN: null,
              showModalVMCameraLiveviewSN: serialNumber,
            })
          );
          // Show Info. of live view (That It'll be available for only 10 minutes)
          setTimeout(() => {
            toast.info(
              tCamera("live_feed_info", {
                seconds: liveviewDurationSeconds,
              })
            );
          }, 3 * 1000);
        }
      }, 10 * 1000); // allowing 10 seconds
    }, 2500); // extra 2.5 seconds
  };

  const handleSubmit = () => {
    // hideModal();
    const cameraDevice = dataDeviceListing?.data?.[0];
    if (cameraDevice?.is_online === "online") {
      dispatch(
        setModalsData({
          ...modalsState,
          showModalVMCameraLiveviewContinueSN: null,
          showModalVMCameraLiveviewSN:
            modalsState.showModalVMCameraLiveviewContinueSN,
        })
      );
    } else {
      dispatch(
        setModalsData({
          ...modalsState,
          showModalVMCameraLiveviewContinueSN: null,
          showModalWakeupCamera: {
            cid: cameraDevice?.cid || "",
            serial_number: cameraDevice?.serial_number || "",
            onSuccessCallback: onCameraWakeupSuccess,
          },
        })
      );
    }
  };

  if (modalsState.showModalVMCameraLiveviewContinueSN === null) return null;

  return (
    <>
      <div
        className="fixed w-full h-screen top-0 left-0 bg-modal-overlay z-overlay"
        onClick={hideModal}
      ></div>
      <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 outline-none focus:outline-none z-modal">
        <div className="relative my-6 mx-auto max-w-[calc(100vw-4rem)] w-[480px] z-modal">
          <form
            className="p-8 bg-white grid grid-cols-6 gap-6 rounded-3xl shadow-2xl"
            onSubmit={handleSubmit}
          >
            <h2 className="col-span-6 text-3xl font-bold text-heading-black">
              {t("heading")}
            </h2>
            <p className="col-span-6 mt-0 leading-relaxed text-gray-500 flex flex-col gap-2">
              <span>
                {t("description_line1", {
                  seconds: liveviewDurationSeconds,
                })}
              </span>
              <span>
                {t("description_line2", {
                  device: modalsState.showModalVMCameraLiveviewContinueSN,
                })}
              </span>
            </p>
            <AdminFormFieldSubmit
              label={t("button_continue")}
              type="button"
              variant="primary"
              onClick={handleSubmit}
              disabled={isFetchingDeviceListing}
            />

            <AdminFormFieldSubmit
              label={tMain("cancel")}
              type="button"
              variant="danger-transparent"
              onClick={hideModal}
            />
          </form>
        </div>
      </div>
    </>
  );
};

export default ModalVMCameraLiveviewContinue;
